import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useIsNarrow, gridCols, LoadError } from './ui';

const API = '/api';

const C = {
  overdue: '#A63A3A',
  pending: '#c9a84c',
  done:    '#2D4A3E',
  late:    '#5C4A2A',
  navy:    '#1a2744',
};

function PerformanceReport() {
  const [summary,            setSummary]            = useState(null);
  const [trend,              setTrend]              = useState([]);
  const [avgByFn,            setAvgByFn]            = useState([]);
  const [overdueByFn,        setOverdueByFn]        = useState([]);
  const [complianceByEntity, setComplianceByEntity] = useState([]);
  const [onboarding,         setOnboarding]         = useState(null);
  const [loading,            setLoading]            = useState(true);
  // PERFFAILSAFE: distinct from `loading`. A failed fetch left every chart's backing array at
  // its empty-[] default and `summary` at null, then loading=false let all six charts render as
  // confident zero-value bars indistinguishable from a genuinely quiet period - a missing load
  // looked exactly like real data.
  const [loadFailed, setLoadFailed] = useState(false);
  const isNarrow = useIsNarrow();

  const load = () => {
    setLoading(true);
    setLoadFailed(false);
    Promise.all([
      axios.get(`${API}/performance/summary`),
      axios.get(`${API}/performance/task-completion-trend`),
      axios.get(`${API}/performance/avg-completion-by-function`),
      axios.get(`${API}/performance/overdue-by-function`),
      axios.get(`${API}/performance/compliance-by-entity`),
      axios.get(`${API}/performance/onboarding-pipeline`),
    ])
      .then(([sumR, trendR, avgR, overdueR, compR, onbR]) => {
        setSummary(sumR.data);
        setTrend(trendR.data);
        setAvgByFn(avgR.data);
        setOverdueByFn(overdueR.data);
        setComplianceByEntity(compR.data);
        setOnboarding(onbR.data);
        setLoading(false);
      })
      .catch(() => { setLoadFailed(true); setLoading(false); });
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return (
    <div style={{ padding: '24px', color: '#aaa', fontStyle: 'italic', fontSize: '14px' }}>
      Loading performance data...
    </div>
  );

  if (loadFailed) return (
    <div style={{ padding: '24px' }}>
      <LoadError message="Could not load performance data." onRetry={load} />
    </div>
  );

  const maxTrend   = Math.max(...trend.map(m => m.done_on_time + m.done_late + m.pending), 1);
  const maxOverdue = Math.max(...overdueByFn.map(f => f.count), 1);
  const BAR_H      = 140;

  return (
    <div style={{ margin: '-20px -24px', padding: '24px 24px 0', background: '#edecea' }}>
      <h2 style={{ fontSize: '16px', fontWeight: 600, color: C.navy, margin: '0 0 20px 0' }}>
        Performance
      </h2>

      {/* ── ROW 1 - 4 KPI cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: gridCols(isNarrow, 'repeat(4, 1fr)'), gap: '14px', marginBottom: '20px' }}>
        {[
          {
            label: 'Avg task completion',
            value: summary ? `${summary.avg_completion_days}d` : '-',
            accent: C.pending,
            sub: 'average days from creation to completion',
          },
          {
            label: 'Overdue rate',
            value: summary ? `${summary.overdue_rate}%` : '-',
            accent: C.overdue,
            sub: 'of all tasks currently overdue',
          },
          {
            label: 'Onboarding rate',
            value: summary ? `${summary.onboarding_rate}%` : '-',
            accent: C.done,
            sub: 'new starter forms fully completed',
          },
          {
            label: 'Compliance rate',
            value: summary ? `${summary.attestation_rate}%` : '-',
            accent: C.late,
            sub: 'compliance attestations submitted',
          },
        ].map(k => (
          <div key={k.label} style={{
            backgroundColor: '#fff', borderRadius: '10px', padding: '18px 20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            borderTop: '0.5px solid #EEE', borderRight: '0.5px solid #EEE',
            borderBottom: '0.5px solid #EEE', borderLeft: `3px solid ${k.accent}`,
          }}>
            <p style={{ fontSize: '11px', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 8px 0', fontWeight: 600 }}>
              {k.label}
            </p>
            <p style={{ fontSize: '28px', fontWeight: 700, margin: '0 0 4px 0', color: k.accent }}>
              {k.value}
            </p>
            <p style={{ fontSize: '11px', color: '#aaa', margin: 0 }}>{k.sub}</p>
          </div>
        ))}
      </div>

      {/* ── ROW 2 - 2 columns ── */}
      <div style={{ display: 'grid', gridTemplateColumns: gridCols(isNarrow, '1fr 1fr'), gap: '16px', marginBottom: '16px', alignItems: 'start' }}>

        {/* LEFT - Stacked bar: task completion trend */}
        <div style={s.card}>
          <h3 style={s.cardTitle}>Task completion: last 6 months</h3>
          <div style={{ display: 'flex', gap: '16px', marginBottom: '14px' }}>
            {[['Done on time', C.done], ['Done late', C.late], ['Pending / Overdue', C.pending]].map(([lbl, col]) => (
              <div key={lbl} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <div style={{ width: '10px', height: '10px', borderRadius: '2px', backgroundColor: col, flexShrink: 0 }} />
                <span style={{ fontSize: '11px', color: '#666' }}>{lbl}</span>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '6px', height: `${BAR_H + 22}px` }}>
            {trend.map(m => {
              const total = m.done_on_time + m.done_late + m.pending;
              const barH  = total > 0 ? Math.max(4, Math.round((total / maxTrend) * BAR_H)) : 0;
              return (
                <div key={m.month} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  {total > 0 ? (
                    <div style={{ width: '100%', height: `${barH}px`, display: 'flex', flexDirection: 'column' }}>
                      {m.pending     > 0 && <div style={{ flex: m.pending,     backgroundColor: C.pending }} />}
                      {m.done_late   > 0 && <div style={{ flex: m.done_late,   backgroundColor: C.late }} />}
                      {m.done_on_time > 0 && <div style={{ flex: m.done_on_time, backgroundColor: C.done }} />}
                    </div>
                  ) : (
                    <div style={{ width: '100%', height: '4px', backgroundColor: '#e8e4dc', borderRadius: '2px' }} />
                  )}
                  <span style={{ fontSize: '10px', color: '#aaa', marginTop: '5px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                    {m.month.split(' ')[0]}
                  </span>
                </div>
              );
            })}
          </div>
          {trend.length === 0 && <p style={s.empty}>No task data available.</p>}
        </div>

        {/* RIGHT - Avg completion by function */}
        <div style={s.card}>
          <h3 style={s.cardTitle}>Average completion time by function</h3>
          {avgByFn.length === 0 ? (
            <p style={s.empty}>No completed tasks found.</p>
          ) : (
            avgByFn.map(item => {
              const dayColor = item.avg_days < 4 ? C.done : item.avg_days <= 6 ? C.pending : C.overdue;
              const trendTxt = item.trend === null
                ? null
                : item.trend > 0
                  ? `+${item.trend}d`
                  : item.trend < 0
                    ? `${item.trend}d`
                    : '-';
              return (
                <div key={item.function} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 0', borderBottom: '0.5px solid #F5F2EE' }}>
                  <span style={{ flex: 1, fontSize: '13px', color: C.navy, fontWeight: 500 }}>
                    {item.function}
                  </span>
                  <span style={{ fontSize: '15px', fontWeight: 700, color: dayColor, minWidth: '42px', textAlign: 'right' }}>
                    {item.avg_days}d
                  </span>
                  {trendTxt !== null && (
                    <span style={{ fontSize: '11px', color: '#aaa', minWidth: '36px', textAlign: 'right' }}>
                      {trendTxt}
                    </span>
                  )}
                </div>
              );
            })
          )}
        </div>

      </div>

      {/* ── ROW 3 - 3 columns ── */}
      <div style={{ display: 'grid', gridTemplateColumns: gridCols(isNarrow, '1fr 1fr 1fr'), gap: '16px', alignItems: 'start' }}>

        {/* LEFT - Overdue tasks by function */}
        <div style={s.card}>
          <h3 style={s.cardTitle}>Overdue tasks by function</h3>
          {overdueByFn.length === 0 ? (
            <p style={s.empty}>No overdue tasks.</p>
          ) : (
            overdueByFn.map(item => (
              <div key={item.function} style={{ marginBottom: '10px' }}>
                <span style={{ fontSize: '12px', color: C.navy, fontWeight: 500, display: 'block', marginBottom: '3px' }}>
                  {item.function}
                </span>
                <div style={{ height: '20px', backgroundColor: '#f0ece6', borderRadius: '4px', overflow: 'hidden' }}>
                  <div style={{
                    width: `${Math.max((item.count / maxOverdue) * 100, 12)}%`,
                    height: '100%',
                    backgroundColor: C.overdue,
                    display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                    paddingRight: '7px', boxSizing: 'border-box',
                  }}>
                    <span style={{ fontSize: '11px', color: '#fff', fontWeight: 600 }}>{item.count}</span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* MIDDLE - Compliance rate by entity */}
        <div style={s.card}>
          <h3 style={s.cardTitle}>Compliance rate by entity</h3>
          {complianceByEntity.length === 0 ? (
            <p style={s.empty}>No compliance data.</p>
          ) : (
            complianceByEntity.map(item => {
              const barColor = item.rate >= 60 ? C.done : item.rate >= 40 ? C.pending : C.overdue;
              const shortName = item.entity
                .replace('Enko Capital ', '')
                .replace(/ \(.*\)$/, '');
              return (
                <div key={item.entity} style={{ marginBottom: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
                    <span style={{ fontSize: '11px', color: C.navy, fontWeight: 500 }}>{shortName}</span>
                    <span style={{ fontSize: '11px', color: '#aaa' }}>{item.compliant}/{item.total}</span>
                  </div>
                  <div style={{ height: '18px', backgroundColor: '#f0ece6', borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{
                      width: `${item.rate || 0}%`,
                      height: '100%',
                      backgroundColor: barColor,
                      display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                      paddingRight: '6px', boxSizing: 'border-box',
                      minWidth: item.rate > 0 ? '32px' : '0',
                    }}>
                      {item.rate > 0 && (
                        <span style={{ fontSize: '10px', color: '#fff', fontWeight: 600 }}>{item.rate}%</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* RIGHT - Onboarding pipeline */}
        <div style={s.card}>
          <h3 style={s.cardTitle}>Onboarding pipeline</h3>
          {!onboarding || onboarding.total === 0 ? (
            <p style={s.empty}>No active employees.</p>
          ) : (
            [
              { label: 'Complete',    value: onboarding.complete,    color: C.done },
              { label: 'In progress', value: onboarding.in_progress, color: C.pending },
              { label: 'Not started', value: onboarding.not_started, color: C.overdue },
            ].map(row => {
              const pct = Math.round((row.value / onboarding.total) * 100);
              return (
                <div key={row.label} style={{ marginBottom: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                    <span style={{ fontSize: '13px', color: C.navy, fontWeight: 500 }}>{row.label}</span>
                    <span style={{ fontSize: '13px', color: '#aaa' }}>{row.value}</span>
                  </div>
                  <div style={{ height: '22px', backgroundColor: '#f0ece6', borderRadius: '5px', overflow: 'hidden' }}>
                    <div style={{
                      width: `${pct}%`,
                      height: '100%',
                      backgroundColor: row.color,
                      display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                      paddingRight: '8px', boxSizing: 'border-box',
                      minWidth: row.value > 0 ? '34px' : '0',
                      transition: 'width 0.3s ease',
                    }}>
                      {row.value > 0 && (
                        <span style={{ fontSize: '11px', color: '#fff', fontWeight: 600 }}>{pct}%</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

      </div>
    </div>
  );
}

const s = {
  card:      { backgroundColor: '#fff', borderRadius: '10px', padding: '20px', marginBottom: 0, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '0.5px solid #EEE' },
  cardTitle: { fontSize: '14px', fontWeight: 600, color: '#1a2744', margin: '0 0 16px 0' },
  empty:     { fontSize: '13px', color: '#667085', fontStyle: 'italic', margin: '4px 0' },
};

export default PerformanceReport;
